import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Outlet, Link } from "react-router-dom";
import { UserIcon } from "./UserIcon";
import { Subscription } from "../../Pages/Private/Settings/Subscription";
import { useLocation } from "react-router-dom";
import { apps, Menu, subMenu, getFirstAccessibleSubMenuDefinition } from "../../Menu";
import logo from "../../../../../public/imgs/logo.png";
import { test } from "../ClientDescription";
import { resolveLoginLanding } from "../../_helpers/resolveLoginLanding";

export const LogoDropdown = ({ navigate, setCurrentMenu, setCurrentApp, user, setCurrentTab }) => {
    const [open, setOpen] = useState(false);
    const dropdownRef = useRef(null);

    const hostname = window.location.hostname;
    const sub_domain = hostname.split(".")[0];

    useEffect(() => {
        function handleClickOutside(event) {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target)
            ) {
                setOpen(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const filteredApp = Object.entries(user.policy.rights)
        .filter(([key, app]) => app.access)
        .map(([key, app]) => ({ key, ...app }))
        .sort(function (a, b) { return a.order < b.order ? -1 : 1 });

    return (
        <div className="relative" ref={dropdownRef}>
            <div
                className="flex items-center gap-2 cursor-pointer p-2 rounded-sm"
                onClick={() => setOpen(!open)}
            >
                <img
                    src={test[sub_domain]?.logo || logo}
                    alt="Logo"
                    className="w-full"
                />
                {filteredApp.length > 1 && (
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4 text-gray-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M19 9l-7 7-7-7"
                        />
                    </svg>
                )}
            </div>

            {open && (
                <div className="absolute z-50 mt-2 w-56 bg-white border rounded-sm shadow-sm">
                    {filteredApp.map((app) => {
                        const appName = apps.find(findApp => findApp.id === app.key.toUpperCase());
                        return <div
                            key={app.key}
                            onClick={() => {
                                setOpen(false);
                                const tab_rights = user.policy.rights[app.key];
                                const filteredMenu = Menu[app.key].filter(tab => tab_rights[tab.id]["access"]);
                                const firstMenu = filteredMenu[0];

                                setCurrentApp(app.key);
                                setCurrentMenu(firstMenu);

                                if (firstMenu.isSubMenu && tab_rights[firstMenu.id]?.sub_menu) {
                                    const firstSub = getFirstAccessibleSubMenuDefinition(
                                        firstMenu.id,
                                        tab_rights[firstMenu.id].sub_menu,
                                    );
                                    if (firstSub) {
                                        setCurrentTab(firstSub);
                                        navigate(firstSub.to);
                                    } else {
                                        setCurrentTab("MAIN");
                                        navigate(firstMenu.to);
                                    }
                                } else {
                                    setCurrentTab("MAIN");
                                    navigate(firstMenu.to);
                                }
                            }}
                            className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                        >
                            {appName?.name}
                        </div>
                    })}
                </div>
            )}
        </div>
    );
};

function getCurrentMenuKeyAndItem(currentPath, menu) {
    for (const key in menu) {
        const match = menu[key].find((item) => item.link?.some(link => currentPath.startsWith(link)) || currentPath.startsWith(item.to));
        if (match) {
            return { key, item: match };
        }
    }
    return { key: null, item: null };
}

export const PrivateLayout = (props) => {
    const { setUser, user } = props;
    const { pathname, hash } = useLocation();
    const { key, item } = getCurrentMenuKeyAndItem(pathname, Menu);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [currentApp, setCurrentApp] = useState(key);
    const [currentMenu, setCurrentMenu] = useState(item);
    const [currentTab, setCurrentTab] = useState("MAIN");
    const navigate = useNavigate();
    const tab_rights = user?.policy?.rights?.[currentApp];
    const menuItems = Menu[currentApp] || [];
    const filteredMenu = menuItems && tab_rights ? menuItems.filter(tab => tab.id && tab_rights[tab.id]?.["access"]) : [];
    const filteredSubMenu = currentMenu?.id && tab_rights?.[currentMenu.id]?.sub_menu
        ? Object.entries(tab_rights[currentMenu.id].sub_menu).filter(([, menu]) => menu.access).map(([key, menu]) => ({ key, ...menu }))
        : [];

    useEffect(() => {

        if (!currentMenu || !currentApp) {
            navigate(resolveLoginLanding(user));
            return;
        }
        // Synchroniser le menu avec le pathname actuel
        const { key: newKey, item: newItem } = getCurrentMenuKeyAndItem(pathname, Menu);
        
        if (newKey && newItem && user?.policy?.rights?.[newKey]) {
            // Mettre à jour currentApp si nécessaire
            setCurrentApp(prevApp => {
                if (prevApp !== newKey) {
                    return newKey;
                }
                return prevApp;
            });
            
            // Mettre à jour currentMenu si nécessaire
            setCurrentMenu(prevMenu => {
                if (prevMenu?.id !== newItem.id) {
                    return newItem;
                }
                return prevMenu;
            });
            
            // Déterminer le currentTab en fonction du pathname
            const tab_rights = user.policy.rights[newKey];
            if (newItem.isSubMenu && tab_rights?.[newItem.id]?.sub_menu) {
                const rightsSub = tab_rights[newItem.id].sub_menu;
                const pathMatches = subMenu.filter(
                    (sub) => sub.page === newItem.id && sub.to.startsWith("/") && pathname.startsWith(sub.to),
                );
                const matchingSubMenu = pathMatches.sort((a, b) => b.to.length - a.to.length)[0];

                if (matchingSubMenu && rightsSub[matchingSubMenu.id]?.access) {
                    setCurrentTab(matchingSubMenu);
                } else {
                    const firstAllowed = getFirstAccessibleSubMenuDefinition(newItem.id, rightsSub);
                    if (firstAllowed) {
                        setCurrentTab(firstAllowed);
                        const urlTabAllowed =
                            matchingSubMenu && rightsSub[matchingSubMenu.id]?.access;
                        if (!urlTabAllowed && !pathname.startsWith(firstAllowed.to)) {
                            navigate(firstAllowed.to);
                        }
                    }
                }
            } else {
                // Si ce n'est pas un sous-menu, utiliser "MAIN" par défaut
                setCurrentTab("MAIN");
            }
        }
    }, [pathname, user]);

    const isPayingCustomer = () => {
        return true;
        // return (
        //     new Date(user.trial_ends_at) >= new Date() ||
        //     user.type === "EMPLOYEE"
        // );
    };

    return (
        <div className="flex h-screen min-h-0 w-full overflow-hidden">
            <div
                className={`fixed inset-y-0 left-0 z-40 w-64 shrink-0 border-r bg-white transform transition-transform duration-200 ease-in-out lg:static lg:translate-x-0 lg:flex lg:flex-col ${sidebarOpen ? "translate-x-0" : "-translate-x-full"
                    }`}
            >
                <div className="flex items-center shrink-0 px-4">
                    <LogoDropdown
                        user={user}
                        navigate={navigate}
                        setCurrentApp={setCurrentApp}
                        setCurrentMenu={setCurrentMenu}
                        filteredSubMenu={filteredSubMenu}
                        setCurrentTab={setCurrentTab}
                    />
                    <div className="flex lg:hidden p-2">
                        <button onClick={() => setSidebarOpen(false)}>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-6 w-6 text-gray-800"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        </button>
                    </div>
                </div>

                <div className="flex flex-col grow pt-2 overflow-y-auto border-x">


                    <div className="flex flex-col flex-1 px-3 mt-6">
                        <div className="space-y-4">
                            <nav className="flex-1 space-y-2 min-w-56 text-black">
                                {user &&
                                    filteredMenu?.map((tab) => {
                                        const activeMenu =
                                            "cursor-pointer mx-2 flex items-center px-2 py-2 text-sm font-medium transition-all duration-200 text-app-primary rounded-lg border-2 border-app-primary group no-underline";
                                        const unActiveMenu =
                                            "cursor-pointer mx-2 flex items-center px-2 py-2 text-sm font-medium transition-all duration-200 text-gray-900 rounded-lg border-2 border-transparent hover:bg-app-primary hover:text-white hover:bg-app-primary group no-underline";
                                        return (
                                            <div key={tab.to}>
                                                {tab.separator && <hr />}
                                                <Link onClick={(e) => {
                                                    e.preventDefault();
                                                    const tab_rights = user.policy.rights[currentApp];
                                                    setCurrentMenu(tab);
                                                    if (tab.isSubMenu && tab_rights[tab.id]?.sub_menu) {
                                                        const firstSub = getFirstAccessibleSubMenuDefinition(
                                                            tab.id,
                                                            tab_rights[tab.id].sub_menu,
                                                        );
                                                        if (firstSub) {
                                                            setCurrentTab(firstSub);
                                                            navigate(firstSub.to);
                                                        } else {
                                                            setCurrentTab("MAIN");
                                                            navigate(tab.to);
                                                        }
                                                    } else {
                                                        setCurrentTab("MAIN");
                                                        navigate(tab.to);
                                                    }
                                                    setSidebarOpen(false);
                                                // }} className={(currentMenu?.link?.includes(tab.to) || currentMenu.to == tab.to) ? activeMenu : unActiveMenu}>
                                                }} className={((pathname == tab.to) || (currentMenu?.link?.includes(tab.to)) || (currentMenu.to == tab.to)) ? activeMenu : unActiveMenu}>
                                                    {typeof tab.svg === "string" ? <svg
                                                        className="shrink-0 mr-4 w-6 h-6"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="currentColor"
                                                        strokeWidth="2"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            d={tab.svg}
                                                        />
                                                    </svg> : tab.svg}
                                                    {tab.title}
                                                </Link>
                                            </div>
                                        );
                                    })}
                            </nav>
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex min-h-0 min-w-0 flex-1 flex-col w-full lg:max-w-custom">
                <div className="flex h-14 shrink-0 lg:justify-end justify-start border-y w-auto">
                    <div className="lg:hidden flex grow items-center p-2">
                        <button
                            onClick={() => setSidebarOpen(!sidebarOpen)}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-6 w-6 text-gray-800"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M4 6h16M4 12h16M4 18h16"
                                />
                            </svg>
                        </button>
                    </div>
                    <UserIcon
                        user={user}
                        setUser={setUser}
                        currentApp={currentApp}
                    />
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto">
                    {isPayingCustomer() ? (
                        <Outlet context={[user, setUser, currentApp, currentMenu, currentTab, setCurrentTab, setCurrentMenu]} />
                    ) : (
                        <Subscription
                            isInContext={false}
                            user={user}
                            setUser={setUser}
                            currentApp={currentApp}
                            currentMenu={currentMenu} currentTab={currentTab}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default PrivateLayout;
